import { createContext, useContext, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useLocation } from "react-router";
import { PRODUCT_KEYS, type ProductKey } from "../../shared/products/keys";
import {
  activeProductFor,
  resolveActiveProduct,
  type ActiveProduct,
} from "../components/products/activeProduct";
import {
  readActiveChoice,
  subscribeLastProduct,
  writeLastProduct,
} from "../components/products/lastProduct";
import { getInstanceIdFromUrl } from "../utils/instanceId";
import { useEnabledProducts } from "./useEnabledProducts";

export {
  activeProductFor,
  defaultProductFor,
  productForcedBy,
  resolveActiveProduct,
  type ActiveProduct,
  type ActiveProductInput,
} from "../components/products/activeProduct";

const noSubscription = () => () => {};

export type ActiveProductState = ActiveProduct & {
  /** The instance the URL is on: the router's own `?instanceid=`, else the app's usual lookup. */
  instanceId: string;
  /** The choice for that instance, unvalidated: this session's, else the stored one (lastProduct). */
  stored: ProductKey | null;
  /** The path this was resolved for. */
  pathname: string;
};

/**
 * The active product at a given location, read from the products the instance has and the user
 * holds (useEnabledProducts) and the stored choice (lastProduct). Re-renders when the stored choice
 * changes in this tab (the switcher writes, then navigates). For a caller that already holds its
 * router location; everyone else uses useActiveProductState.
 */
export function useActiveProductAt(pathname: string, search: string): ActiveProductState {
  const instanceId = new URLSearchParams(search).get("instanceid") || getInstanceIdFromUrl() || "";
  const { products, ready, has, holdsRole } = useEnabledProducts();
  const stored = useSyncExternalStore(instanceId ? subscribeLastProduct : noSubscription, () =>
    instanceId ? readActiveChoice(instanceId) : null,
  );
  const heldKey = PRODUCT_KEYS.filter((key) => has(key) && holdsRole(key)).join(",");
  return useMemo(() => {
    const held = heldKey ? (heldKey.split(",") as ProductKey[]) : [];
    return {
      ...resolveActiveProduct({ pathname, stored, ready, enabled: products, held }),
      instanceId,
      stored,
      pathname,
    };
  }, [pathname, stored, ready, products, heldKey, instanceId]);
}

/** The active product as the shell sees it, at the router's current location. */
export function useActiveProductState(): ActiveProductState {
  const { pathname, search } = useLocation();
  return useActiveProductAt(pathname, search);
}

/**
 * Stores the product a product's own page forces (skyAdvisor's PackRouteGuard), so a shared page
 * opened next keeps it. Called once, by AppLayout.
 *
 * - Only on a path TRANSITION, once per (instance, path) visit: never on a re-render of the same
 *   page. A shared page in between ends the visit, so returning to a page stores again.
 *   The switcher writes its choice and then navigates; the router commits the new URL in a
 *   transition, so the write first re-renders the app on the OLD page. Writing on every render
 *   stored that old page's product straight back (fix round 1, C-1).
 * - Only once the route guard has admitted the page (`admitted`): a page the guard refuses, a
 *   leaver's `/performance` for instance, never becomes the remembered product (review M-2).
 * - Only for a user holding more than one product, and only a product they hold: a single-product
 *   user has nothing to remember, and nothing invalid is ever written.
 */
export function useActiveProductSync(active: ActiveProductState, admitted: boolean): void {
  const { forced, held, instanceId, pathname } = active;
  const heldKey = held.join(",");
  const synced = useRef<string | null>(null);
  useEffect(() => {
    // A shared page (or `/`) forces nothing and ends the current product page: coming back to the
    // same product page later (browser Back) is a new transition and stores again (fix round 2).
    if (forced === null) {
      synced.current = null;
      return;
    }
    if (!instanceId || !admitted) return;
    // Nothing to decide until the user's products are known (a hire page is admitted before they
    // resolve); the page is then still unsynced, so it is decided once they arrive.
    const holds = heldKey ? heldKey.split(",") : [];
    if (holds.length < 2) return;
    const key = `${instanceId}|${pathname}`;
    if (synced.current === key) return;
    synced.current = key;
    if (holds.includes(forced) && readActiveChoice(instanceId) !== forced) {
      writeLastProduct(instanceId, forced);
    }
  }, [instanceId, forced, heldKey, admitted, pathname]);
}

/**
 * The active product AppLayout resolved, for components that have no store of their own to read
 * (the header wordmark). Absent outside the app shell.
 */
export const ActiveProductContext = createContext<ProductKey | null>(null);

/**
 * The active product for the shell's wordmark. Inside AppLayout, its resolved choice; outside it
 * (a component rendered on its own) the URL rule that held before.
 */
export function useActiveProduct(): ProductKey {
  const provided = useContext(ActiveProductContext);
  const { pathname } = useLocation();
  return provided ?? activeProductFor(pathname);
}
